<template>
  <div v-if="contacts">
    <qd-checkbox class="mgb-30px" v-model="showArchive">
        {{ $t('archiverContact.showArchive', { nombre: archiveContacts.length}) }}
    </qd-checkbox>
    <v-client-table :data="filteredContacts"
                    :columns="columns"
                    :options="options">
      <template slot="civiliteId" slot-scope="props">
        <div class="has-text-left">
          {{ civilite(props.row) }}
        </div>
      </template>
      <template slot="typeContact" slot-scope="props">
        <div class="has-text-left">
          {{  getTypeContact(props.row.typesContactIds) }}
        </div>
      </template>
      <template slot="supprimer" slot-scope="{ row }">
        <div class="has-text-centered" v-if="!disabled">
          <delete-list-button
            :query="() => removeContact(row.id)"
            :validation="validation"
            :permissions="permsTiers.supprimer"
            :title="$t('supprimerContact.delete')"
            :subtitle="$t('supprimerContact.irreversible')"
            :successMessage="$t('supprimerContact.messages.suppression')"
            @done="removeContactList(row.id)"/>
        </div>
      </template>
      <template slot="archiver" slot-scope="{ row }">
        <div class="has-text-centered" v-if="!disabled">
          <archive-list-button
            :query="() => archiver([row.id])"
            :permissions="permsTiers.supprimer"
            :title="$t(`general.archiver.${row.archive ? 'reactive': 'titre'}`,
              { type: $t('supprimerContact.type') })"
            :successMessage="$t('archiverContact.archiveSuccess')"
            @done="archiveContactList(row.id, row.archive)"
            :actif="!row.archive"/>
        </div>
      </template>
      <template slot="modifier" slot-scope="props">
        <div class="has-text-centered" v-if="!disabled">
          <a @click="modifierContact(props.row)">
            <b-icon
              icon="pencil-box-outline"
              type="is-success">
            </b-icon>
          </a>
        </div>
      </template>
      <template slot="email" slot-scope="props">
        <div class="has-text-centered" v-if="!disabled">
          <a class="lien">
            {{props.row.email}}
          </a>
        </div>
      </template>
    </v-client-table>
    <div v-if="!disabled">
      <button @click="ajouterContact()"
         class="button is-primary mgt-10px">
        {{ $t('editionClient.actions.addContact')}}
      </button>
      <b-modal v-model="ajouterContactSelectionne"
               has-modal-card
               class="modal">
        <app-ajouter-contact
          :contact="contact"
          :contacts="contacts"
          :isEditContact="isEditContact"
          :tiers="client"
          :idTiers="client.id"
          :isFromBailleur="isFromBailleur"/>
      </b-modal>
    </div>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import {
  TEXTS, RESULTS_PER_PAGE, NO_PER_PAGE_VALUE, SORT_ICON,
} from '@/configuration/clientTableConf';
import contactGenerer from '@/services/tiers/genererContact';
import * as types from '@/store/modules/clients-mutation-type';
import * as typesParametres from '@/store/modules/parametres-mutation-type';
import messageErreur from '@/services/general/messageErreur';
import { remove, archiver } from '@/api/contacts/index';
import { TIERS } from '@/configuration/permissionsBodacConf';
import archiveListButton from '@/components/general/button/archiveListButton';
import { columnsSplice } from '@/services/general/columns';
import validation from '@/services/contact/validation';
import deleteListButton from '@/components/general/button/deleteListButton';
import AjouterContact from './ajouterContact';

const permissions = createNamespacedHelpers('permissions');
const Clients = createNamespacedHelpers('clients');
const Parametres = createNamespacedHelpers('parametres');

export default {
  name: 'ClientEditContacts',
  props: ['contacts', 'disabled', 'client', 'isFromBailleur'],
  components: {
    appAjouterContact: AjouterContact,
    archiveListButton,
    deleteListButton,
  },
  data() {
    return {
      contact: contactGenerer(),
      ajouterContactSelectionne: false,
      isEditContact: false,
      permsTiers: TIERS,
      showArchive: false,
      remove,
      archiver,
      validation,
    };
  },
  computed: {
    ...Parametres.mapGetters({
      parametre: 'parametre',
    }),
    civilites() {
      return this.parametre(typesParametres.PARAMETRES_KEYS.civilite);
    },
    typeContact() {
      return this.parametre(typesParametres.PARAMETRES_KEYS.typeContact);
    },
    filteredContacts() {
      return this.showArchive ? this.contacts : this.contacts.filter(c => !c.archive);
    },
    archiveContacts() {
      return this.contacts.filter(c => c.archive);
    },
    columns() {
      const columns = ['civiliteId', 'nom', 'prenom', 'fonction', 'service', 'typeContact', 'telephone', 'portable', 'email', 'modifier', 'archiver', 'supprimer'];
      if (this.disabled) {
        columnsSplice(columns, 'modifier');
        columnsSplice(columns, 'archiver');
        columnsSplice(columns, 'supprimer');
      }
      return columns;
    },
    options() {
      const texts = cloneDeep(TEXTS);
      texts.count = '';
      return {
        sortIcon: SORT_ICON,
        headings: {
          civiliteId: this.$i18n.t('listeContacts.table.civility'),
          nom: this.$i18n.t('listeContacts.table.name'),
          typeContact: this.$i18n.t('listeContacts.table.typeContact'),
          prenom: this.$i18n.t('listeContacts.table.firstname'),
          fonction: this.$i18n.t('listeContacts.table.role'),
          service: this.$i18n.t('listeContacts.table.office'),
          telephone: this.$i18n.t('listeContacts.table.phone'),
          portable: this.$i18n.t('listeContacts.table.cellPhone'),
          email: this.$i18n.t('listeContacts.table.email'),
          supprimer: '',
          modifier: '',
          archiver: '',
        },
        filterable: false,
        perPageValues: NO_PER_PAGE_VALUE,
        perPage: RESULTS_PER_PAGE,
        texts: texts,
        sortable: ['civiliteId', 'nom', 'prenom', 'fonction', 'service', 'telephone', 'portable', 'email'],
        columnsClasses: {
          portable: 'colonneTelephone',
          telephone: 'colonneTelephone',
          modifier: 'colonneSelected',
          archiver: 'colonneSelected',
          supprimer: 'colonneSelected',
        },
      };
    },
  },
  methods: {
    ...Clients.mapMutations({
      setClientCourant: types.SET_CLIENT_COURANT,
    }),
    ...permissions.mapActions({
      showPopUp: 'showPopup',
    }),
    ...Parametres.mapActions({
      loadParametres: 'loadParametres',
    }),
    getTypeContact(typeContactId) {
      const typeContact = this.typeContact.filter((el) => typeContactId.includes(el.id));
      if (typeContact.length > 0) {
        return typeContact.map((el) => el.libelle).join(', ');
      }
      return '';
    },
    removeContactList(id) {
      const index = this.contacts.findIndex(c => c.id === id);
      if (index < 0) return;
      this.contacts.splice(index, 1);
    },
    archiveContactList(id, archive) {
      const index = this.contacts.findIndex(c => c.id === id);
      if (index < 0) return;
      this.$set(this.contacts[index], 'archive', !archive);
    },
    modifierContact(contact) {
      this.contact = contact;
      this.isEditContact = true;
      this.ajouterContactSelectionne = true;
    },
    ajouterContact() {
      this.contact = contactGenerer();
      this.ajouterContactSelectionne = true;
    },
    civilite(contact) {
      if (!contact.civiliteId) return '';
      const civlib = this.civilites.find((element) => {
        return contact.civiliteId === element.id;
      }).libelle;
      return civlib;
    },
    removeContact(id) {
      if (id) {
        remove(id);
      }
    },
  },
  async mounted() {
    const loadingComponent = this.$buefy.loading.open();
    try {
      await this.loadParametres([typesParametres.PARAMETRES_KEYS.civilite,
        typesParametres.PARAMETRES_KEYS.typeContact]);
      loadingComponent.close();
    } catch (e) {
      messageErreur(this.$i18n);
      loadingComponent.close();
      throw e;
    }
  },
};
</script>

<style scoped>
</style>
